import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { generateId } from 'common/utils/string';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  ActivityPurchaseAddOnsItemFormType,
  ActivityPurchaseFormType,
  ActivityPurchaseGuestDetailsFormType,
  ActivityPurchaseGuestsFormType,
  ActivityPurchasesItemFormType,
} from '../component/form-type';

interface Props {
  activityPurchase?: ActivityPurchaseModel;
  activitySale?: ActivitySaleModel;
  defaultValues?: ActivityPurchaseFormType;
}

export function addNewAddOns(): ActivityPurchaseAddOnsItemFormType {
  return {
    id: undefined,
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    price: undefined,
  } as never as ActivityPurchaseAddOnsItemFormType;
}

export function addNewGuestDetail(): ActivityPurchaseGuestDetailsFormType {
  return {
    activityId: '',
    activityName: '',
    customId: generateId(),
    description: '',
    type: '',
    date: undefined,
    qty: undefined,
    price: undefined,
    totalPrice: undefined,
  } as never as ActivityPurchaseGuestDetailsFormType;
}

export function addNewGuest(): ActivityPurchaseGuestsFormType {
  return {
    activityPurchaseGuestDetails: [addNewGuestDetail()],
    customId: generateId(),
    guestId: '',
    guestName: '',
  } as never as ActivityPurchaseGuestsFormType;
}

export function addNewPurchase(): ActivityPurchasesItemFormType {
  return {
    id: undefined,
    customId: generateId(),
    activityPurchaseAddOns: [],
    activityPurchaseGuests: [addNewGuest()],
    isGroupedPrice: false,
  } as never as ActivityPurchasesItemFormType;
}

export function tourListToActivityPurchase(
  tourList: TourModel,
): ActivityPurchaseFormType {
  const transactionAt = new Date();

  const defaultValues: ActivityPurchaseFormType = {
    orderNumber: '',
    saleTotal: 0,
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: tourList.id,
    tourRefId: tourList.refId,
    type: 'tour',
    activitySale: undefined,
    activityPurchase: undefined,
    total: 0,
    activityPurchases: [addNewPurchase()], //activity purchases will be fill with select sales
  };

  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { activityPurchase, activitySale } = props;
  const defaultValues = React.useMemo<ActivityPurchaseFormType>(() => {
    const transactionAt = activityPurchase?.transactionAt || new Date();
    if (props.defaultValues) return props.defaultValues;
    if (activitySale) {
      return {
        orderNumber: '',
        saleTotal: activitySale.total,
        agentId: '',
        agentStatus: '',
        remainingTransactionQuota: null,
        isExceedingQuota: false,
        code: activitySale.code || '',
        commission: 0,
        description: '',
        discount: activitySale.discount || 0,
        invoiceId: activitySale.id || '',
        invoiceRefId: activitySale.refId || '',
        needApproval: false,
        otherCost: activitySale.otherCost || 0,
        paymentChannelId: '',
        refId: undefined,
        parentRefId: '',
        tempTransactionAt: transactionAt,
        transactionAt,
        tourId: '',
        tourRefId: '',
        type: 'sales',
        activitySale,
        activityPurchase,
        total: 0,
        activityPurchases: [addNewPurchase()], //activity purchases will be fill with select sales
      };
    }
    const agent = activityPurchase?.agent;
    const paymentChannel = activityPurchase?.paymentChannel;
    const sale = activityPurchase?.sale;
    const tour = activityPurchase?.tour;

    const activityPurchases: ActivityPurchasesItemFormType[] =
      activityPurchase?.activityPurchases?.map((ap) => {
        const activityPurchaseAddOns: ActivityPurchaseAddOnsItemFormType[] =
          ap.activityPurchaseAddOns.map((apao) => {
            const guest = apao.guest;
            return {
              customId: generateId(),
              description: apao.description || '',
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: apao.id,
              price: apao.price,
            };
          });

        const activityPurchaseGuests: ActivityPurchaseGuestsFormType[] =
          ap.activityPurchaseGuests.map((apg) => {
            const guest = apg.guest;

            const activityPurchaseGuestDetails: ActivityPurchaseGuestDetailsFormType[] =
              apg.activityPurchaseGuestDetails.map((apd) => {
                const activity = apd.activity;
                return {
                  activityId: activity.id,
                  activityName: activity.name,
                  customId: generateId(),
                  date: apd.date,
                  description: apd.description || '',
                  price: apd.price,
                  qty: apd.qty,
                  totalPrice: apd.totalPrice,
                  type: activity.type,
                };
              });

            return {
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              customId: generateId(),
              id: apg.id,
              activityPurchaseGuestDetails,
            };
          });

        return {
          customId: generateId(),
          id: ap.id,
          isGroupedPrice: ap.isGroupedPrice,
          activityPurchaseAddOns,
          activityPurchaseGuests,
        };
      }) ?? [addNewPurchase()];

    return {
      orderNumber: activityPurchase?.number || '',
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      paymentChannelId: paymentChannel?.id || '',
      code: activityPurchase?.code || '',
      commission: activityPurchase?.agentCommission?.amount || 0,
      description: activityPurchase?.description || '',
      discount: activityPurchase?.discount || 0,
      refId: activityPurchase?.refId,
      parentRefId: activityPurchase?.parentPurchase?.id,
      invoiceId: sale?.id || '',
      tourId: tour?.id || '',
      tourRefId: activityPurchase?.tour?.refId || '',
      invoiceRefId: activityPurchase?.invoiceRefId || '',
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      otherCost: activityPurchase?.otherCost || 0,
      total: activityPurchase?.total || 0,
      saleTotal: sale?.total || 0,
      type: activityPurchase?.type || 'sales',
      activityPurchases,

      activityPurchase,
      activitySale,
    };
  }, [activityPurchase, activitySale, props.defaultValues]);

  return defaultValues;
}
